import React, { createContext, useContext, useEffect, useState } from 'react';
import plainText from './plainText';
import textarea from './textarea';
import select from './select';
import checkbox from './checkbox';
import date from './date';
import upload from './upload';
import submit from './submit';
import contact from './contact';
import child from './child';
import hazkara from './hazkara';
import calendar from './calendar';
import formBlock from './formBlock';

import steps from './steps';
import condition from './condition';
import identity from './identity';
import product from './product';
import seatPicker from './seatPicker';
import productInfo from './productInfo';
import productTotal from './productTotal';
import freeAmount from './freeAmount';
import customPrice from './customPrice';
import { quoteCustomPrice } from './customPriceRules';
import paiementMethod from './paiementMethod';
import promo from './promo';

import contactDdn from './contactDdn';
import contactSocialId from './contactSocialId';

import { notification } from 'antd';
import { injectLayoutParams } from '../layout';
import moment from 'moment';
import dayjs from 'dayjs';
import empty from './empty';
import aiContent from './aiContent';
import currencies from 'currencies.json';
import { t } from "@lingui/core/macro"


var BASE_URL = "https://api.kehila.syn-assist.fr/v1"
if (process.env.NODE_ENV !== "production") {
    BASE_URL = "https://kehilaapi.syn-assist.fr/v1"
}

export const requireEtatCountries=["US","CA","AU","BR","MX","IN","CN","JP"];


export const FormFieldPath = createContext({ path: [], listFieldProps: null })

/**
 * Hook utilitaire pour les plugins de formulaire.
 *
 * CANAL UNIQUE : l'identité d'un champ est portée par UN SEUL vecteur, `path`.
 * Chaque container (productInfo, contact, child, formBlock) ÉTEND `path` avec son
 * propre segment et re-provide le contexte ; aucun n'écrase quoi que ce soit.
 *   - productInfo pousse l'index d'occurrence du Form.List : path = [...path, index]
 *   - contact pousse son namespace : path = [...path, "[CONTACT]_xxx"]
 *   - une feuille produit son name = [...path, finalName]
 *
 * Plus de second canal `listFieldProps.name`. `listFieldProps` n'est conservé que
 * pour rétro-compat du spread Ant des feuilles ({...(listFieldProps||{})}), mais il
 * ne porte AUCUNE identité (Ant v5/v6 a supprimé `fieldKey` ; ce spread est inerte).
 *
 * DEUX chemins, rôles DISJOINTS :
 *  - `path` : chemin RELATIF au Form.List (productInfo). Sert UNIQUEMENT à buildName
 *    (le name Antd) : Antd préfixe lui-même par productId, donc path NE contient PAS
 *    le productId. Ne pas y toucher → aucune régression sur les feuilles.
 *  - `absolutePath` : chemin ABSOLU dans `formValues` (INCLUT les productId). Sert
 *    UNIQUEMENT à naviguer dans formValues (conditions scopées, extraction de sous-
 *    valeurs). Jamais utilisé pour construire un name.
 *  - `storePath` : chemin ABSOLU dans le STORE DU <Form> ANTD RACINE. Identique à
 *    absolutePath partout… SAUF dans un bloc de formulaire (formBlock) : là,
 *    absolutePath est volontairement RELATIF au sous-store du bloc (ses conditions
 *    naviguent dans l'occurrence extraite), alors que les champs Antd, eux, sont
 *    enregistrés dans le Form racine sous le chemin complet (valuesPath du bloc).
 *    storePath sert donc aux écritures/lectures programmatiques via l'instance
 *    du Form racine (form.getFieldValue / form.setFieldValue), ex. « Copier
 *    depuis… » des blocs contact/enfant. Jamais utilisé pour construire un name.
 *
 * @returns {{
 *   path: Array,                              // chemin relatif Form.List (pour buildName)
 *   absolutePath: Array,                      // chemin absolu dans formValues (pour conditions/scope)
 *   storePath: Array,                         // chemin absolu dans le store du Form Antd racine
 *   listFieldProps: object | null,            // compat spread Ant (inerte, sans rôle d'identité)
 *   buildName: (finalName) => Array | string, // helper pour produire le name Antd complet
 *   inList: boolean                           // true si une occurrence (index numérique) est dans le path
 * }}
 */
export function useFormPath() {
    const ctx = useContext(FormFieldPath)
    const path = Array.isArray(ctx?.path) ? ctx.path : []
    // Défaut = path (à la racine et pour les contextes legacy, les deux coïncident).
    const absolutePath = Array.isArray(ctx?.absolutePath) ? ctx.absolutePath : path
    // Défaut = absolutePath (hors bloc de formulaire, les deux coïncident).
    const storePath = Array.isArray(ctx?.storePath) ? ctx.storePath : absolutePath
    const inList = path.some(seg => typeof seg === "number")

    // Conservé uniquement pour compat avec le spread des feuilles. Aucune identité.
    const listFieldProps = ctx?.listFieldProps || null

    const buildName = (finalName) => {
        // finalName : string ("[TEXT]_xxx") ou array (["[CONTACT]_xxx", "homme"])
        const tail = Array.isArray(finalName) ? finalName : [finalName]
        const segments = [...path, ...tail]
        return segments.length === 1 ? segments[0] : segments
    }

    return { path, absolutePath, storePath, listFieldProps, buildName, inList }
}

export function removeFieldType(fieldName) {
    return fieldName.replace(/^\[[A-Za-z0-9_]*\]_/, "")
}

// ─────────────────────────────────────────────────────────────────────────────
//  « Copier depuis… » — réutilisation d'une personne déjà saisie (blocs
//  [CONTACT]_ et [CHILD]_). Helpers PURS (aucune dépendance React/Antd), testés
//  unitairement. La copie est PONCTUELLE : elle préremplit les champs du bloc
//  cible, qui restent modifiables (aucun miroir, aucun champ grisé).
// ─────────────────────────────────────────────────────────────────────────────

export const PERSON_BLOCK_PREFIX = {
    CONTACT: "[CONTACT]_",
    CHILD: "[CHILD]_",
}

// Champs du bloc identité recopiables dans un bloc contact (« Moi-même »).
// Volontairement limité aux champs civils/adresse : les champs personnalisés de
// l'identité appartiennent à SON contexte, pas à celui du contact.
export const IDENTITY_COPY_KEYS = ["homme", "nom", "prenom", "email", "tel", "adresse", "postal", "ville", "etat", "pays", "note_adresse"]

// Profondeur max du parcours de formValues (racine → produit → occurrence →
// bloc de formulaire → … ). Garde-fou contre un objet cyclique ou exotique.
const PERSON_WALK_MAX_DEPTH = 8

function isPlainObject(v) {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return false
    const proto = Object.getPrototypeOf(v)
    return proto === Object.prototype || proto === null
}

function cleanString(v) {
    return typeof v === "string" ? v.trim() : ""
}

/**
 * Libellé humain d'un bloc personne : « Prénom Nom », sinon l'email, sinon le
 * téléphone. Chaîne vide si rien d'exploitable.
 */
export function personLabel(values) {
    if (!isPlainObject(values)) return ""
    const name = [cleanString(values.prenom), cleanString(values.nom)].filter(Boolean).join(" ")
    return name || cleanString(values.email) || cleanString(values.tel)
}

/**
 * Empreinte de dédoublonnage : deux blocs qui décrivent la même personne (même
 * prénom+nom, email, tél et date de naissance) ne sont proposés qu'une fois.
 */
export function personFingerprint(values) {
    if (!isPlainObject(values)) return ""
    const ddn = values.ddn instanceof Date ? values.ddn.toISOString() : (values.ddn ? String(values.ddn) : "")
    return [cleanString(values.prenom), cleanString(values.nom), cleanString(values.email), cleanString(values.tel), ddn]
        .map(s => s.toLowerCase().replace(/\s+/g, " "))
        .join("|")
}

/**
 * Vrai si le bloc « est » encore la personne de l'identité principale. Sert à
 * masquer le sélecteur de relation d'un contact copié depuis « Moi-même » tant
 * qu'il n'a pas été modifié (une personne n'a pas de relation avec elle-même).
 *
 * Règle : quand l'identité porte un nom, c'est le PRÉNOM+NOM qui tranche
 * (insensible à la casse/espaces) — pas l'email ni le téléphone, souvent
 * partagés dans un foyer (un conjoint copié puis renommé doit retrouver son
 * sélecteur de relation). Sans nom côté identité, repli sur email puis téléphone.
 */
export function looksLikeSamePerson(values, identity) {
    if (!isPlainObject(values) || !isPlainObject(identity)) return false
    const norm = (s) => cleanString(s).toLowerCase().replace(/\s+/g, " ")
    const telNorm = (s) => cleanString(s).replace(/[\s\-().]/g, "")
    const identityName = norm(identity.prenom) + "|" + norm(identity.nom)
    if (identityName !== "|") {
        return identityName === norm(values.prenom) + "|" + norm(values.nom)
    }
    if (norm(values.email) && norm(values.email) === norm(identity.email)) return true
    return !!telNorm(values.tel) && telNorm(values.tel) === telNorm(identity.tel)
}

function samePath(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((seg, i) => String(seg) === String(b[i]))
}

/**
 * Recense, dans les valeurs du Form racine, les blocs personne du type demandé
 * (« CONTACT » ou « CHILD ») déjà renseignés — à la racine, dans les occurrences
 * de produits (tableaux) et dans les blocs de formulaire imbriqués — en excluant
 * le bloc appelant (`selfStorePath`) et les doublons (même empreinte).
 *
 * @param {object} formValues   form.getFieldsValue() du Form Antd RACINE
 * @param {"CONTACT"|"CHILD"} type
 * @param {Array} selfStorePath chemin absolu (store racine) du bloc appelant
 * @returns {Array<{storePath:Array, label:string, values:object}>}
 */
export function collectPersonBlocks(formValues, type, selfStorePath = []) {
    const prefix = PERSON_BLOCK_PREFIX[type]
    if (!prefix || !isPlainObject(formValues)) return []

    const found = []
    const seen = new Set()

    const visit = (node, storePath, depth) => {
        if (depth > PERSON_WALK_MAX_DEPTH || node === null || typeof node !== "object") return
        if (node instanceof Date) return
        // dayjs / moment : objets non plats, jamais des conteneurs de blocs.
        if (!Array.isArray(node) && !isPlainObject(node)) return

        if (Array.isArray(node)) {
            node.forEach((occ, index) => visit(occ, [...storePath, index], depth + 1))
            return
        }

        for (const key of Object.keys(node)) {
            const value = node[key]
            const childPath = [...storePath, key]

            if (typeof key === "string" && key.startsWith(prefix)) {
                if (!isPlainObject(value)) continue
                if (samePath(childPath, selfStorePath)) continue
                const label = personLabel(value)
                if (!label) continue
                const fp = personFingerprint(value)
                if (seen.has(fp)) continue
                seen.add(fp)
                found.push({ storePath: childPath, label, values: value })
                // Un bloc personne ne contient jamais d'autre bloc personne.
                continue
            }

            // L'identité principale n'est pas un bloc réutilisable ici (elle est
            // proposée à part, « Moi-même », uniquement pour les contacts).
            if (key === "identity" && depth === 0) continue

            if (Array.isArray(value)) {
                // Occurrences d'un produit : antd conserve (preserve) les entrées
                // au-delà de la quantité courante quand le visiteur la baisse, et
                // productInfo ne les retire jamais du store → on ne propose que les
                // occurrences encore rendues (index < `<productId>_quantity`).
                const quantity = node[key + "_quantity"]
                const limit = typeof quantity === "number" ? quantity : Infinity
                value.forEach((occ, index) => { if (index < limit) visit(occ, [...childPath, index], depth + 1) })
                continue
            }

            visit(value, childPath, depth + 1)
        }
    }

    visit(formValues, [], 0)
    return found
}

/**
 * Construit les valeurs à écrire dans le bloc cible à partir d'une source.
 *  - `fromIdentity` : source = bloc identité → uniquement IDENTITY_COPY_KEYS
 *    (jamais `_relation` : une personne n'est pas en relation avec elle-même).
 *  - sinon : tout l'objet source SAUF `id` (rattachement CRM propre à la
 *    source ; le serveur ré-associe de lui-même par tél/email ou parents+prénom).
 *  - les champs FIXÉS par l'organisation sur la cible (`fixedGenre`,
 *    `fixedRelation`) ne sont jamais écrasés.
 *  - les valeurs VIDES (`undefined`, `null`, chaîne blanche — un Input vidé
 *    stocke "" et PhoneInput émet null) sont ignorées : un préremplissage
 *    n'efface jamais rien et ne déclenche pas d'erreur « obligatoire » ;
 *    objets/tableaux PLATS clonés en surface (Date/dayjs partagés tels quels :
 *    immuables dans nos usages).
 */
export function buildPersonCopyValues(sourceValues, options = {}) {
    if (!isPlainObject(sourceValues)) return {}
    const { fromIdentity = false, fixedGenre = false, fixedRelation = false } = options

    const out = {}
    const keys = fromIdentity
        ? IDENTITY_COPY_KEYS.filter(k => Object.prototype.hasOwnProperty.call(sourceValues, k))
        : Object.keys(sourceValues).filter(k => k !== "id")

    for (const k of keys) {
        if (fixedGenre && k === "homme") continue
        if (fixedRelation && k === "_relation") continue
        if (fromIdentity && k === "_relation") continue
        const v = sourceValues[k]
        if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) continue
        if (Array.isArray(v)) out[k] = [...v]
        else if (isPlainObject(v)) out[k] = { ...v }
        else out[k] = v
    }
    return out
}

/**
 * Clés des champs antd ENREGISTRÉS directement sous `targetStorePath` (ce que le
 * bloc cible rend réellement : civilité, nom, adresse seulement si `full`,
 * champs perso qu'il déclare…). Source : `form.getFieldsError()` liste toutes
 * les entités de champ avec leur chemin ABSOLU, y compris dans un Form.List.
 * Renvoie null si l'instance ne l'expose pas (double de test) → pas de filtre.
 */
function registeredKeysUnder(form, targetStorePath) {
    if (typeof form.getFieldsError !== "function") return null
    let entries
    try { entries = form.getFieldsError() } catch (e) { return null }
    if (!Array.isArray(entries)) return null
    const keys = new Set()
    for (const entry of entries) {
        const name = Array.isArray(entry?.name) ? entry.name : (entry?.name !== undefined ? [entry.name] : null)
        if (!name || name.length !== targetStorePath.length + 1) continue
        if (!samePath(name.slice(0, targetStorePath.length), targetStorePath)) continue
        keys.add(String(name[targetStorePath.length]))
    }
    return keys
}

/**
 * Applique une copie ponctuelle sur le Form Antd RACINE puis resynchronise le
 * moteur (conditions). `form` = instance reçue par un render-prop de Form.Item
 * (donc l'instance RACINE, même à l'intérieur d'un bloc de formulaire).
 *
 * Le bloc cible est REMPLACÉ (jamais fusionné) : aucune valeur de l'ancien
 * occupant — email/tél absents de la source, `_relation`, `id` CRM — ne
 * survit ; seules `options.keepKeys` (champs fixés par l'org) sont conservées.
 * Seules les clés que le bloc cible REND (champs enregistrés) sont écrites :
 * dans une occurrence Form.List, antd soumet le sous-arbre brut, une clé
 * invisible partirait donc dans la soumission et sur le contact CRM.
 *
 * ⚠️ Toujours écrire au chemin ABSOLU complet (storePath, avec l'index
 * d'occurrence) : une écriture sans index recrée le bug historique des
 * occurrences stockées en objet (cf. api/migrations/fix_form_occurrences.js).
 *
 * @returns {string[]} clés effectivement écrites
 */
export function applyPersonCopy(form, formPlugin, targetStorePath, values, options = {}) {
    if (!form || typeof form.setFieldValue !== "function" || !Array.isArray(targetStorePath) || targetStorePath.length === 0) return []
    const { keepKeys = [] } = options

    const registered = registeredKeysUnder(form, targetStorePath)
    const written = {}
    for (const k of Object.keys(values || {})) {
        if (registered && !registered.has(k)) continue
        written[k] = values[k]
    }
    const keys = Object.keys(written)
    if (!keys.length) return []

    const current = form.getFieldValue(targetStorePath)
    const kept = {}
    if (isPlainObject(current)) {
        for (const k of keepKeys) if (current[k] !== undefined) kept[k] = current[k]
    }
    form.setFieldValue(targetStorePath, { ...kept, ...written })

    // Les champs copiés qui portaient déjà une erreur (« obligatoire ») sont
    // revalidés pour l'effacer — uniquement ceux réellement écrits (donc à
    // valeur non vide) ; un nom inconnu du Form est simplement ignoré.
    try {
        const p = form.validateFields(keys.map(k => [...targetStorePath, k]))
        if (p && typeof p.catch === "function") p.catch(() => {})
    } catch (e) { /* jamais bloquant */ }

    if (formPlugin && typeof formPlugin.syncFromForm === "function") {
        formPlugin.syncFromForm(form)
    }
    return keys
}

export function DefaultValueProvider({ value, onChange, defaultValue, children }) {
    const hasDefault = defaultValue !== undefined && defaultValue !== null;

    // Une copie peut remplacer le bloc sans son pays : réenregistrer le défaut
    // affiché à chaque valeur absente, sans remplacer un choix explicite.
    React.useEffect(() => {
        if (value == null && hasDefault && onChange) {
            onChange(defaultValue);
        }
    }, [value, hasDefault, defaultValue, onChange]);

    const handleChange = (newVal) => {
        const next = newVal?.target?.value ?? newVal;
        onChange?.(next);
    };

    return React.Children.map(children, (child) =>
        React.isValidElement(child)
            ? React.cloneElement(child, {
                value: value ?? (hasDefault ? defaultValue : ""), // fallback d’affichage
                onChange: handleChange,
            })
            : child
    );
}

export function FixedValueProvider({ value, defaultValue, onChange, children }) {
    const hasDefault = defaultValue !== undefined;

    // 🔁 Si un defaultValue est défini : on force la valeur du parent à rester égale
    React.useEffect(() => {
        if (hasDefault && value !== defaultValue && onChange) {
            onChange(defaultValue);
        }
    }, [hasDefault, value, defaultValue, onChange]);

    const handleChange = (newVal) => {
        const next = newVal?.target?.value ?? newVal;

        if (hasDefault) {
            // Ignore toute tentative de modification
            if (onChange && next !== defaultValue) {
                onChange(defaultValue);
            }
        } else {
            // Pass-through standard
            onChange?.(next);
        }
    };

    const currentValue = hasDefault ? defaultValue : value;

    return React.Children.map(children, (child) =>
        React.isValidElement(child)
            ? React.cloneElement(child, {
                value: currentValue,
                onChange: handleChange,
            })
            : child
    );
}



export function MomentToDate({value, onChange, children}) {

    // Ant Design's picker adapter requires immutable Dayjs values. Returning
    // Moment for stored Date values lets calendar calculations mutate answers.
    const toPicker = value => dayjs(typeof value?.toDate === "function" ? value.toDate() : value)
    const convertToPicker = (v) => {
        if (!v) return null
        if (Array.isArray(v)) {
            if (v.length !== 2 || !v[0] || !v[1]) return null
            return [toPicker(v[0]), toPicker(v[1])]
        }
        return toPicker(v)
    }
    const convertToDate = (v) => {
        if (!v) {
            return null
        }
        const toDate = (x) => {
            if (!x) return null
            if (typeof x.toDate === "function") return x.toDate()
            if (x instanceof Date) return x
            return null
        }
        if (Array.isArray(v)) {
            if (v.length !== 2) return null
            return [toDate(v[0]), toDate(v[1])]
        }
        return toDate(v)
    }

    return React.Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a
        // typescript error too.
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                value: convertToPicker(value),
                onChange: (v) => onChange(convertToDate(v))
            });
        }
        return child;
    });
}


class EventEmitter {
    constructor() {
        this._events = new Map();
    }

    on(event, callback) {
        if (!this._events.has(event)) {
            this._events.set(event, new Set());
        }
        this._events.get(event).add(callback);
    }

    off(event, callback) {
        const callbacks = this._events.get(event);
        if (callbacks) {
            callbacks.delete(callback);
            if (callbacks.size === 0) this._events.delete(event);
        }
    }

    emit(event, data) {
        const callbacks = this._events.get(event);
        if (callbacks) {
            for (const cb of callbacks) {
                try {
                    cb(data);
                } catch (e) {
                    console.error(`[EventEmitter] Error in '${event}':`, e);
                }
            }
        }
    }

    removeAll(event) {
        this._events.delete(event);
    }
}

export const CONDITIONS = [
    `Est égal à`,
    `Est supérieur ou égal à`,
    `Est inférieur ou égal à`,
    `N'est pas égal à`,
    `Est vide`,
    `N'est pas vide`,
    `Est coché`,
]

export default class FormPlugin extends EventTarget {

    VALIDATION = [
        `Alphabétique`,
        `Alphanumérique`,
        `Numérique`,
        `Téléphone`,
        `Email`,
        `Url`,
        `IBAN`,
    ]

    DATE_FILTER_SELECT = [
        `Antérieur à aujourd'hui`,
        `Ultérieur à aujourd'hui`,
        `Aujourd'hui & antérieur à aujourd'hui`,
        `Aujourd'hui & ultérieur à aujourd'hui`,
    ]

    DATE_PICKER = [
        `Date & Heure`,
        `Uniquement les Années`,
        `Uniquement les Mois`,
        `Uniquement l'heure`,
    ]

    UPLOAD_TYPE = [
        `Image`,
        `Fichier Pdf`,
        `Fichier Zip`,
    ]


    get RELATIONS_OPTIONS() { return [
        {
            labelH: t`Le Conjoint`,
            labelF: t`La Conjointe`,
            value: "MARI"
        },
        {
            labelH: t`Le Père`,
            labelF: t`La Mère`,
            value: "PERE"
        },
        {
            labelH: t`Le Fils`,
            labelF: t`La Fille`,
            value: "ENFANT"
        },
        {
            labelH: t`Le Frere`,
            labelF: t`La Soeur`,
            value: "FRERE"
        },
        {
            labelH: t`Le Grand Père`,
            labelF: t`La Grand Mère`,
            value: "GP"
        },
        {
            labelH: t`Le Petit Fils`,
            labelF: t`La Petite Fille`,
            value: "PF"
        },
        {
            labelH: t`l'Ex Conjoint`,
            labelF: t`l'Ex Conjointe`,
            value: "EX"
        },
        {
            labelH: t`Le Beau Frere`,
            labelF: t`La Belle Sœur`,
            value: "BF"
        },
        {
            labelH: t`Le Cousin`,
            labelF: t`La Cousine`,
            value: "COUSIN"
        },
        {
            labelH: t`l'ami`,
            labelF: t`l'amie`,
            value: "AMI"
        },
    ] }

    namespaces = [{key: "/", formPrefix: null}]

    org = null

    formRef = null
    formItems = []
    formValues = {}

    timeoutAlertIdentity = null
    editorRef = null
    savedTree = {}

    live = false
    prefixName = null
    formData = null

    initialPlugins = []
    formPluginsByNamespace = new Map()
    contactPluginsByNamespace = new Map()
    tmpRerender = []
    prevConditionalResult = []
    prevProductsQuantity = {}

    productsStockRef=[]

    nodeExtraDatas=[]

    BASE_URL=BASE_URL

    on(eventName, callback) {
        this.addEventListener(eventName, callback);
    }

    off(eventName, callback) {
        this.removeEventListener(eventName, callback);
    }

    setEditor(ref) {
        this.editorRef = ref
    }

    setTree(t) {
        this.savedTree = t
    }

    setFormRef(ref) {
        this.formRef = ref
    }

    setOrg(org) {
        this.org = org
    }

    // Verrouillage de l'identité civile (compte titulaire connecté). Quand true, le
    // plugin form-identity/ rend civilité/nom/prénom/email/tél en `disabled` : le
    // submit est rattaché au contact du compte (garde A7), donc ces champs ne doivent
    // pas être modifiables (cohérence identité↔contact). L'adresse reste éditable.
    setLockIdentity(locked) {
        this.lockIdentity = !!locked
    }

    isIdentityLocked(){
        return this.lockIdentity === true
    }

    // Contrôle « utiliser mon compte » exposé au plugin form-identity/ (web). Permet au
    // bloc identité d'afficher une alerte à deux états (compte actif / désactivé) et de
    // basculer via les callbacks. `hasAccount` : un compte titulaire est connecté ;
    // `disabled` : le visiteur a cliqué « ce n'est pas moi » (prefill coupé). En admin,
    // jamais posé → getAccountControl() renvoie un objet inerte (aucune alerte).
    setAccountControl(control){
        this.accountControl = control || null
    }

    getAccountControl(){
        return this.accountControl || {hasAccount: false, disabled: false, onDisable: null, onEnable: null}
    }

    getDeviseSymbol(){
        const devise=this.org?.devise || "eur"
        return currencies.currencies.find(i=>i.code===devise.toUpperCase())?.symbol
    }

    currencyFormat(amount) {
        const language=this.org?.language || "fr"
        const devise=this.org?.devise || "eur"

        return new Intl.NumberFormat(language, {
            style: 'currency',
            currency: devise.toUpperCase()
        }).format(amount)
    }

    getCountry(){
        return this.org?.country || "FR"
    }

    setFormValues(values) {
        //console.log("formValues",values)
        this.formValues = values
        this.dispatchEvent(new CustomEvent("formValuesChanges", {detail:values}));
    }

    // Plugin RACINE du formulaire. Reste null sur le plugin racine lui-même ;
    // posé par formBlock sur le plugin interne d'un bloc de formulaire (qui n'a
    // ni formRef ni formValues globales).
    rootFormPlugin = null

    /**
     * Resynchronise le moteur après une écriture PROGRAMMATIQUE dans le Form Antd
     * (form.setFieldValue ne déclenche PAS onValuesChange, donc ni setFormValues ni
     * checkConditions). On repart toujours du plugin RACINE : c'est lui qui porte
     * formValues et l'éditeur ; un bloc de formulaire est re-notifié par
     * l'événement formValuesChanges (cf. formBlock).
     */
    syncFromForm(form) {
        const root = this.rootFormPlugin || this
        if (!form || typeof form.getFieldsValue !== "function") return
        try {
            // Même périmètre que le 2e argument d'onValuesChange (champs enregistrés).
            root.setFormValues(form.getFieldsValue())
            root.checkConditions()
        } catch (e) {
            console.error("[formPlugin] syncFromForm", e)
        }
    }

    addProductStockRef(id,callback){
        this.productsStockRef=[...this.productsStockRef.filter(i=>i.id!==id),{id:id,callback:callback}]
    }

    constructor(editorRef = null, formData = null, tree = null, prefixName = null, live = false) {
        super();
        this.editorRef = editorRef
        this.formData = formData
        this.savedTree = tree
        this.prefixName = prefixName
        this.live = live
        this.stockOverride = false
    }

    // Optional standalone runtime; the site and admin keep their existing defaults.
    setRuntime(runtime) {
        this.runtime = runtime || null
        if (runtime?.baseUrl) this.BASE_URL = runtime.baseUrl
    }

    notify(kind, options) {
        if (this.runtime?.notify) return this.runtime.notify(kind, options)
        return notification[kind === 'warning' ? 'warn' : kind](options)
    }

    // Activation réservée au rendu de saisie interne ; les formulaires publics
    // ne reçoivent jamais cette option de leur wrapper.
    setStockOverride(enabled) {
        const next = this.formData?.manual === true && enabled === true
        if (next === this.stockOverride) return
        this.stockOverride = next
        for (const ref of this.productsStockRef) ref.callback()
    }

    isSubmitErrored(data) {
        if (this.formData?.type === "COMPLEX") {

            let findQuantity = false
            for (var p of this.formData.products) {
                if (data[p.id + "_quantity"] > 0) {
                    findQuantity = true
                    break
                }
            }

            if (this.formData.products.length === 0 || this.formData.products.every(p => p.active === false)) {
                findQuantity = true
            }

            if (!findQuantity && !this.formData?.allowNoProduct) {
                this.notify('error', {
                    message: t`Vous devez choisir minimum 1 article.`,
                    placement: "bottomRight"
                })
                return true
            }

            const totalAmount = calculPrice(data, this.formData, this.org?.devise) / 100
            if (!findQuantity && totalAmount < 1 && !this.formData?.allowZeroAmount) {
                this.notify('error', {
                    message: t`Vous devez choisir minimum 1 article.`,
                    placement: "bottomRight"
                })
                return true
            }
        }

        return false
    }

    registerNamespace(key, formPrefix) {
        const index = this.namespaces.findIndex(i => i.key === key)
        if (index !== -1) {
            this.namespaces[index].formPrefix = formPrefix
        } else {
            this.namespaces.push({key: key, formPrefix: formPrefix})
        }
    }

    getFormPlugins(namespace) {
        if (this.formPluginsByNamespace.has(namespace)) return this.formPluginsByNamespace.get(namespace)
        const plugins = injectLayoutParams([
            formBlock(this, namespace),
            plainText(this, namespace),
            textarea(this, namespace),
            select(this, namespace),
            checkbox(this, namespace),
            date(this, namespace),
            upload(this, namespace),
            contact(this, namespace),
            child(this, namespace),
            hazkara(this, namespace),
            submit(this, namespace),

            identity(this, namespace),
            freeAmount(this, namespace),
            customPrice(this, namespace),
            paiementMethod(this, namespace),
            promo(this, namespace),
            product(this, namespace),
            seatPicker(this, namespace),
            productInfo(this, namespace),
            productTotal(this, namespace),
            steps(this, namespace),
            condition(this, namespace),
            (Array.isArray(this.formData?.calendars) && this.formData.calendars.length > 0 && calendar(this, namespace)),

            empty(this, namespace),
            aiContent(this, namespace),
        ].filter(i => i))
        this.formPluginsByNamespace.set(namespace, plugins)
        return plugins
    }

    getContactFormPlugins(namespace) {
        if (this.contactPluginsByNamespace.has(namespace)) return this.contactPluginsByNamespace.get(namespace)
        const plugins = injectLayoutParams([
            contactDdn(this, namespace),
            contactSocialId(this, namespace),
        ])
        this.contactPluginsByNamespace.set(namespace, plugins)
        return plugins
    }

    onAddFormPlugin = (ref, id, key) => {
        if (!this.formItems.some(i => i.id == id)) {
            if (!this.live) {
                /*
                if (key.includes("form-identity")) {
                    var identityForms = [ref]
                    var allowed = true

                    for (let p of this.searchPluginKeys("form-identity")) {
                        if (identityForms) {
                            allowed = false
                        }
                        identityForms.push(p)
                    }

                    if (!allowed && !this.timeoutAlertIdentity) {
                        this.timeoutAlertIdentity = setTimeout(() => {
                            this.timeoutAlertIdentity = null
                            this.notify('warning', {
                                message: "Le Block " + ref.pluginConfig.title + " n'est utilisable qu'une seule fois par formulaire."
                            })
                        }, 250)
                    }

                    if (!allowed) {
                        ref.remove()
                        return false
                    }
                }

                 */
            }

            this.formItems.push({ref: ref, id: id, key: key})
            return true

        } else {
            this.formItems = this.formItems.map(i => {
                if (i.id === id) {
                    i.data = ref.data
                }
                return i
            })
            return false
        }
    }

    registerExtraData(nodeId,extraData={},key=null){
        const _index=this.nodeExtraDatas.findIndex(i=>i.nodeId===nodeId && i.key===key)
        if(_index!==-1){
            this.nodeExtraDatas[_index].data=extraData
        }else{
            this.nodeExtraDatas.push({nodeId:nodeId,key:key,data:extraData})
        }
    }

    getExtraData(nodeId,key=null){
        return this.nodeExtraDatas.find(i=>i.nodeId===nodeId && i.key===key)?.data
    }


    async validateForm(callback) {
        if (!this.formRef) {
            return false
        }
        try {
            await this.formRef.validateFields()
            callback()
        } catch (e) {
            return false
        }
    }

    getFormFieldValue(field) {
        if (!this.formRef) {
            return false
        }
        return this.formRef.getFieldValue(field)
    }


    getAlivePlugins() {
        var alives = []
        for (var child of parsePlugins(this.savedTree)) {
            if (this.formItems.some(i => i.id == child.id)) {
                alives.push(child.id)
            }
        }
        return alives
    }


    searchPluginKey(key) {
        for (var child of parsePlugins(this.savedTree)) {
            let plugin = this.formItems.find(i => i.id === child.id)
            if (plugin && plugin.key.includes(key)) {
                return plugin
            }
        }
        return null
    }

    searchPluginKeys(key) {
        let plugins = []
        for (var child of parsePlugins(this.savedTree)) {
            let plugin = this.formItems.find(i => i.id === child.id)
            if (plugin && plugin.key.includes(key)) {
                plugins.push(plugin)
            }
        }
        return plugins
    }

    getPluginData(id) {
        for (var child of parsePlugins(this.savedTree)) {
            if (child.plugin) {

                let prop = "default"
                if (!child?.dataI18n?.default) {
                    prop = Object.keys(child.dataI18n || {})[0]
                }

                if (child?.dataI18n?.[prop]?.id === id) {
                    //console.log(child.dataI18n)
                    return child.dataI18n[prop]
                }
            }
        }
        return null
    }


    getPluginsMem(namespace) {
        return [...this.initialPlugins, ...this.getFormPlugins(namespace)]
    }

    getPlugins(initialPlugins = [], namespace = "/") {
        // Explicit initialization may change the form definition. During render,
        // getPluginsMem reuses component types so field state/focus survives.
        this.formPluginsByNamespace.clear()
        this.contactPluginsByNamespace.clear()
        this.initialPlugins = initialPlugins
        return [...this.initialPlugins, ...this.getFormPlugins(namespace)]
    }


    checkFormPlugins() {
        var alives = this.getAlivePlugins()
        var deadPlugins = this.formItems.filter(i => !alives.includes(i.id))
        deadPlugins.forEach(i => this.onRemoveFormPlugin(i.id))
        //console.log(this.formItems)
    }


    onRemoveFormPlugin(id) {
        this.formItems = this.formItems.filter((i) => i.id !== id)
    }


    getFormFields(maxNamespace = null) {
        var fields = []
        const plugins = parsePlugins(this.savedTree)
        for (var item of this.formItems) {

            var plugin = plugins.find(i => i.id === item.id)

            /*
            const pluginNamespace="/"+plugin.plugin.id.split("/")[1]
            //console.log("pluginNamespace",pluginNamespace)

            if(maxNamespace && !maxNamespace.includes(pluginNamespace)){
                continue
            }

             */

            let data = null

            let prop = "default"
            if (!plugin?.dataI18n?.default) {
                prop = Object.keys(plugin?.dataI18n || {})[0]
            }

            if (plugin?.dataI18n?.[prop]) {
                data = plugin.dataI18n[prop]
            }

            // Dédup par fieldKey : deux noeuds DIFFERENTS peuvent porter la meme
            // etiquette (champ duplique par copier-coller, ou bloc repete qui
            // reenregistre ses champs sous un nouveau nodeId a chaque occurrence).
            // La dedup de formItems se fait sur l'id du noeud, jamais sur le
            // fieldKey — sans ce filtre le Select des conditions repete la meme
            // entree autant de fois qu'il y a d'occurrences.
            if (data?.fieldKey && !fields.includes(data.fieldKey)) {
                fields.push(data.fieldKey)
            }
        }
        return fields
    }


    getFormFieldsV2(namespace = null) {
        // namespace peut arriver a null/undefined (plugin hors produit) : ne pas
        // appeler replaceAll dessus.
        if(!namespace || namespace==="/"){
            namespace=null
        }else{
            namespace=namespace.replaceAll("/","")
        }

        var fields = []
        const plugins = parsePlugins(this.savedTree)
        for (var item of this.formItems) {

            var plugin = plugins.find(i => i.id === item.id)


            let data = null

            let prop = "default"
            if (!plugin?.dataI18n?.default) {
                prop = Object.keys(plugin?.dataI18n || {})[0]
            }

            if (plugin?.dataI18n?.[prop]) {
                data = plugin.dataI18n[prop]
            }

            if (data?.fieldKey) {
                let label=data.fieldKey

                let _keys=(item.key||"").split("/").filter(i=>i)
                if(_keys.length>1){
                    const _namespace=_keys[1]
                    if(_namespace!==namespace){
                        continue
                    }
                    // Le produit peut avoir ete supprime du formulaire alors que
                    // le champ existe encore : on garde le libelle brut.
                    const product=this.formData?.products?.find(i=>i.id===_namespace)
                    if(product){
                        label="<"+product.label+"> : "+label
                    }
                }

                // Meme dedup que getFormFields, sur la valeur reellement utilisee
                // par la condition (le fieldKey).
                if(fields.some(i=>i.value===data.fieldKey)){
                    continue
                }

                fields.push({label:label,value:data.fieldKey})
            }
        }
        return fields
    }

    hookValueEditor(value) {
        if (!value) {
            return value
        }

        let hookValue = value
        hookValue = migrateNamespace(hookValue)
        //console.log(hookValue)

        for (var id of this.tmpRerender) {
            hookValue = updateDataTree(hookValue, id, "force_render", new Date().getTime())
        }

        if (this.formData?.manual) {
            hookValue = manualDataTree(hookValue)
        }

        this.tmpRerender = []
        return hookValue
    }

    checkConditions() {
        var needRender = false

        //FORCE RERENDER PRODUCT INFO WHEN QUANTITY CHANGE
        if (this.formData.products) {
            var needRenderProducts = false
            for (var p of this.formData.products) {
                const index = p.id + "_quantity"
                var quantity = this.formValues[index]
                if (quantity !== this.prevProductsQuantity[index]) {
                    needRenderProducts = true
                    this.prevProductsQuantity[index] = quantity
                }
            }

            if (needRenderProducts) {
                parsePlugins(this.savedTree).forEach(p => {
                    if (p.plugin.id.includes("form-product-info") || p.plugin.id.includes("form-product-total")) {
                        needRender = true
                        this.tmpRerender.push(p.id)
                    }
                })
            }
        }

        //console.log("checkConditions")
        var conditonPlugins = parsePlugins(this.savedTree).filter(p => p.plugin.id.includes("form-condition"))
        //console.log("conditonPlugins",conditonPlugins)


        for (var cp of conditonPlugins)
        {
            let prop = "default"
            if (!cp.dataI18n.default) {
                prop = Object.keys(cp.dataI18n)[0]
            }

            const language=this.org?.language
            if(language && cp.dataI18n[language]) {
                prop=language
            }

            let data = cp.dataI18n[prop]

            // SIGNAL de re-render : empreinte de TOUTES les valeurs des champs
            // déclencheurs, collectées à tous les niveaux (racine + chaque occurrence
            // de Form.List). On ne compare PLUS le résultat racine de calculCondition
            // (aveugle aux champs imbriqués → restait constant → re-render jamais
            // déclenché pour une condition dans une occurrence). Le calcul d'affichage
            // par occurrence reste fait dans le Renderer de condition.js (absolutePath).
            const signature = conditionSignature(this.formValues, data?.conditions)

            // On conserve le résultat évalué au scope racine dans `results` (rétro-compat
            // tests/debug). Il ne sert PLUS de critère de re-render.
            const result = calculCondition(this.formValues, data?.default, data?.conditions, data?.action)

            const prev = this.prevConditionalResult.find(i => i.id === cp.id)
            if (prev) {
                if (prev.signature !== signature) {
                    needRender = true
                    this.tmpRerender.push(cp.id)
                }
                prev.signature = signature
                prev.results = [result]
            } else {
                needRender = true
                this.prevConditionalResult.push({id: cp.id, signature: signature, results: [result]})
            }

        }

        //console.log("needRender",needRender)

        if (needRender) {
            this.editorRef.forceUpdate()
        }
    }


    async getAvailableProduct(productId, value = 0,propagation=true) {
        if (this.formData?.manual === true && this.stockOverride) return Infinity
        const url = this.BASE_URL + "/forms/stock/" + productId
        const res = await fetch(url, {
            method: "POST",
            body: JSON.stringify(this.formValues),
            headers: {"Content-type": "Application/json"}
        })
        const data = await res.json()

        if (data.total === null) {
            return Infinity
        }

        if(propagation && data.needPropagation){
            for(let refreshP of this.productsStockRef){
                if(refreshP.id !== productId){
                    //console.log("refreshP.callback()",refreshP.id,productId,refreshP.id !== productId)
                    refreshP.callback()
                }
            }
        }

        return data.total - value
    }

    async getAvailableCalendarSlots(calendarId, month, year) {
        const url = this.BASE_URL + "/forms/calendar/" + calendarId + "/slots/" + month + "/" + year
        const req = await fetch(url)
        const res = await req.json()
        return res.data.map(i => new Date(i)) || []
    }

    rerenderProducts() {
        parsePlugins(this.savedTree).forEach(p => {
            if (p.plugin.id.includes("form-product-info") || p.plugin.id.includes("form-product-total")) {
                this.tmpRerender.push(p.id)
            }
        })
        this.editorRef.forceUpdate()
    }

    getCalendarsValues() {
        let data = []

        for (let ck of Object.keys(this.formValues).filter(i => i.startsWith("[CALENDAR]_"))) {
            data.push({key: ck, value: this.formValues[ck]})
        }

        return data
    }
}


//UTILS

export function parsePlugins(tree) {
    var plugins = []
    if (!tree) {
        return []
    }

    var children = []
    if (Array.isArray(tree.rows) && tree.rows.length > 0) {
        children = tree.rows
    }

    if (Array.isArray(tree.cells) && tree.cells.length > 0) {
        children = tree.cells
    }

    while (children.length > 0) {
        var next = []
        for (var child of children) {

            if (child.plugin) {
                plugins.push(child)
            }

            if (Array.isArray(child.rows) && child.rows.length > 0) {
                next = next.concat(child.rows)
            }

            if (Array.isArray(child.cells) && child.cells.length > 0) {
                next = next.concat(child.cells)
            }

            children = next
        }
    }

    return plugins
}

export function getPluginField(tree, fieldKey) {
    for (var child of parsePlugins(tree)) {
        if (child.plugin) {

            let prop = "default"
            if (!child.dataI18n.default) {
                prop = Object.keys(child.dataI18n)[0]
            }

            if (child?.dataI18n[prop]?.fieldKey === fieldKey) {
                return child
            }
        }
    }
    return null
}

function updateDataTree(tree, id, property, value) {
    if (tree.id === id) {
        if (typeof tree?.dataI18n === "object" && Object.keys(tree.dataI18n).length > 0) {
            //console.log(tree.dataI18n)
            let prop = "default"
            if (!tree.dataI18n.default) {
                prop = Object.keys(tree.dataI18n)[0]
            }
            if (tree.dataI18n[prop]) {
                tree.dataI18n[prop][property] = value;
            }
        }
    }

    if (Array.isArray(tree.cells)) {
        for (var i = 0; i < tree.cells.length; i++) {
            tree.cells[i] = updateDataTree(tree.cells[i], id, property, value);
        }
    }

    if (Array.isArray(tree.rows)) {
        for (var i = 0; i < tree.rows.length; i++) {
            tree.rows[i] = updateDataTree(tree.rows[i], id, property, value);
        }
    }

    return tree;
}


// Clés de style "décoratives" (couleurs, fonds, overlays, CSS custom) injectées
// par layout.js. En mode manual, on les strip pour garantir la lisibilité du
// formulaire dans le modal admin (sinon : texte blanc sur fond blanc, etc.).
// On garde les clés structurelles : _padding-*, _width, _height, _border-radius,
// _element-align, _overflow, _hidden-module — elles ne posent pas de pb visuel.
const MANUAL_STRIP_STYLE_KEYS = [
    "_color",
    "_formLabelColor",
    "_background-color",
    "_background-image",
    "_background-size",
    "_background-position",
    "_background-darken",
    "_background-lighten",
    "_background-custom-css",
];

function manualDataTree(tree) {
    //console.log(tree)

    if (typeof tree?.dataI18n === "object" && Object.keys(tree.dataI18n).length > 0) {
        //console.log(tree.dataI18n)
        let props = Object.keys(tree.dataI18n)
        for (let prop of props) {
            if (tree.dataI18n[prop]) {
                tree.dataI18n[prop]["required"] = false;
                for (let k of MANUAL_STRIP_STYLE_KEYS) {
                    delete tree.dataI18n[prop][k];
                }
            }
        }
    }

    if (typeof tree?.plugin === "object" && !tree.plugin.id.startsWith("form")) {
        tree.plugin.id = "form-empty"
        tree.dataI18n = {}
    }

    if (Array.isArray(tree.cells)) {
        for (var i = 0; i < tree.cells.length; i++) {
            tree.cells[i] = manualDataTree(tree.cells[i]);
        }
    }

    if (Array.isArray(tree.rows)) {
        for (var i = 0; i < tree.rows.length; i++) {
            tree.rows[i] = manualDataTree(tree.rows[i]);
        }
    }

    return tree;
}

function migrateNamespace(tree) {
    if (tree.plugin && tree.plugin.id.includes("form") && !tree.plugin.id.includes("/")) {
        tree.plugin.id = tree.plugin.id + "/";
    }

    if (tree.rows) {
        tree.rows = tree.rows.map(i => migrateNamespace(i))
    }

    if (tree.cells) {
        tree.cells = tree.cells.map(i => migrateNamespace(i))
    }

    return tree;
}


function getFlatFormValues(formValues = {}) {
    let _formValues = []

    for (let k of Object.keys(formValues || {})) {
        if (!formValues[k]) {
            continue
        }

        if (typeof formValues[k] !== "object") {
            _formValues.push({key: k, value: formValues[k]})
        } else {
            _formValues = _formValues.concat(getFlatFormValues(formValues[k]))
        }
    }

    return _formValues
}

// ── Signal de re-render des conditions ───────────────────────────────────────
// On NE compare PAS le résultat évalué au scope racine (path=[]) : il est aveugle
// aux champs vivant dans une occurrence de Form.List (formValues[productId][i][...]),
// donc restait constant → la condition imbriquée n'était JAMAIS re-rendue (LE BUG).
//
// À la place on compare une EMPREINTE des valeurs brutes de tous les champs
// déclencheurs de la condition, collectées à TOUS les niveaux de formValues
// (racine + chaque occurrence + contacts imbriqués). Le calcul d'affichage
// PAR OCCURRENCE reste fait dans le Renderer de condition.js (via absolutePath).
// Ici on ne fait que détecter "une variable a bougé quelque part" → on re-render
// le nœud (force_render NON destructif).
//
// ⚠️ On NE réutilise PAS getFlatFormValues : elle ignore les valeurs falsy
// (`if (!formValues[k]) continue`, false/0/""/null) — or les conditions EMPTY /
// CHECKED / NOT EMPTY dépendent EXACTEMENT de ces valeurs. Les ignorer
// provoquerait un SOUS-DÉCLENCHEMENT (transition false→undefined, ""→null…),
// interdit. On collecte donc tout, falsy compris, encodés distinctement.
function collectFieldValues(formValues, wantedFields) {
    const acc = []

    const walk = (scope, path) => {
        if (!scope || typeof scope !== "object") {
            return
        }
        for (const key of Object.keys(scope)) {
            const value = scope[key]

            if (wantedFields.has(removeFieldType(key))) {
                // pathKey = chemin d'accès COMPLET depuis la racine → unique et
                // stable par occurrence, indépendant de l'ordre d'itération des clés.
                // Séparateur \0 : ne peut jamais apparaître dans un fieldKey, donc
                // deux chemins distincts ne peuvent pas produire la même signature.
                acc.push({ k: path.concat(key).join("\0"), v: value })
            }

            if (value && typeof value === "object") {
                if (Array.isArray(value)) {
                    for (let i = 0; i < value.length; i++) {
                        // On inclut `key` (nom du tableau) AVANT l'index, sinon deux
                        // produits distincts produisent des pathKeys identiques.
                        walk(value[i], path.concat(key, String(i)))
                    }
                } else {
                    walk(value, path.concat(key))
                }
            }
        }
    }

    walk(formValues, [])

    // Tri déterministe par chemin : l'empreinte ne change QUE si une valeur change.
    acc.sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : 0))
    return acc
}

// Empreinte sérialisable de toutes les valeurs déclencheuses d'une condition,
// tous niveaux confondus. Deux empreintes égales ⇒ aucun champ référencé n'a
// changé ⇒ pas de re-render nécessaire.
function conditionSignature(formValues, conditions) {
    if (typeof formValues !== "object" || formValues === null || !Array.isArray(conditions)) {
        return ""
    }

    const wantedFields = new Set()
    for (const c of conditions) {
        if (c && typeof c.field === "string") {
            wantedFields.add(c.field)
        }
    }
    if (wantedFields.size === 0) {
        return ""
    }

    const collected = collectFieldValues(formValues, wantedFields)

    // undefined → sentinel distinct de null et de "" : sans ça JSON.stringify émet
    // `null` pour undefined dans un array, confondant "vidé" et "absent".
    try {
        return JSON.stringify(
            collected.map(e => [e.k, e.v === undefined ? "\0undef" : e.v])
        )
    } catch (e) {
        // Valeur non sérialisable (improbable) → force le re-render (jamais de faux négatif).
        return "\0nonserializable:" + new Date().getTime()
    }
}

// Cherche un champ (par son fieldKey nu, sans préfixe de type) au NIVEAU DIRECT
// d'un objet de scope. Pas de descente en profondeur : la remontée des scopes
// ancêtres (getScopeChain) gère le fait de "monter d'un cran". Retourne
// { found, value } ; found=false si le champ n'est pas une clé directe de ce scope.
function findFieldInScope(scope, field) {
    if (!scope || typeof scope !== "object" || Array.isArray(scope)) {
        return { found: false }
    }
    for (const k of Object.keys(scope)) {
        if (removeFieldType(k) === field) {
            return { found: true, value: scope[k] }
        }
    }
    return { found: false }
}

// Construit la liste des scopes ancêtres en suivant `path`, du PLUS PROCHE au
// plus lointain. Ex path=["prodId", 0] → [ formValues.prodId[0], formValues.prodId, formValues ].
// On ne descend que dans NOTRE branche : jamais dans une autre occurrence.
function getScopeChain(formValues, path) {
    const chain = []
    let cursor = formValues
    const nodes = [formValues]
    for (const seg of (Array.isArray(path) ? path : [])) {
        if (cursor && typeof cursor === "object" && cursor[seg] !== undefined) {
            cursor = cursor[seg]
            nodes.push(cursor)
        } else {
            break
        }
    }
    // Du plus proche (dernier traversé) au plus lointain (racine).
    for (let i = nodes.length - 1; i >= 0; i--) {
        chain.push(nodes[i])
    }
    return chain
}

export function calculCondition(formValues, defaultAction, conditions, action, path = []) {
    //console.group(defaultAction,conditions,action)

    if(typeof formValues !== "object"){
     formValues={}
    }

    if (!Array.isArray(conditions)) {
        return defaultAction
    }

    // Résout la valeur d'un champ depuis la branche de la condition : on remonte
    // les scopes ancêtres (occurrence → produit → racine) et on prend le PREMIER
    // qui contient le champ. Jamais de saut vers une occurrence sœur.
    const scopeChain = getScopeChain(formValues, path)
    const resolveFieldValue = (field) => {
        for (const scope of scopeChain) {
            const r = findFieldInScope(scope, field)
            if (r.found) return { found: true, value: r.value }
        }
        return { found: false }
    }

    var results = []
    for (var c of conditions) {
        let operator = "&&"

        if (!c) {
            results.push({bool: false, operator: operator})
            continue
        }

        if (c.next === "OR") {
            operator = "||"
        }

        const resolved = resolveFieldValue(c.field)

        if (!resolved.found) {
            results.push({bool: false, operator: operator})
            continue
        }

        var fieldValue = resolved.value

        if (typeof fieldValue == "string") {
            fieldValue = fieldValue.trim()
        }

        var finalValue = fieldValue
        if (!Array.isArray(fieldValue)) {
            finalValue = [fieldValue]
        }

        var matchValues = c.values || []

        //console.log(finalValue,matchValues,c)

        var bool = false
        switch (c.type) {
            case CONDITIONS[0]: //EQUAL
                bool = finalValue.some(i => matchValues.includes(i))
                break
            case CONDITIONS[1]: //GTE
                bool = finalValue.some(i => matchValues.some(j => i >= j))
                break
            case CONDITIONS[2]: //LTE
                bool = finalValue.some(i => matchValues.some(j => i <= j))
                break
            case CONDITIONS[3]: //NOT EQUAL
                bool = finalValue.some(i => matchValues.every(j => i != j))
                break
            case CONDITIONS[4]: //EMPTY
                bool = (fieldValue === "" || fieldValue === null || fieldValue === undefined)
                break
            case CONDITIONS[5]: //NOT EMPTY
                bool = (fieldValue !== "" && fieldValue !== null && fieldValue !== undefined)
                break
            case CONDITIONS[6]: //CHECKED
                bool = (fieldValue === true)
                break
        }
        //console.log(bool)

        results.push({bool: bool, operator: operator})
    }

    if (results.length === 0) {
        return defaultAction
    }

    // Évalue l'expression booléenne en respectant la priorité de && sur ||
    // (équivalent au comportement de l'ancien eval() sur "true && false || true")
    // Étape 1 : grouper par "ET" (chaque groupe est une suite d'opérandes liés par &&)
    // Étape 2 : combiner les groupes avec "OU"
    let groups = [[results[0].bool]]
    for (let i = 1; i < results.length; i++) {
        const prevOperator = results[i - 1].operator
        if (prevOperator === "||") {
            groups.push([results[i].bool])
        } else {
            groups[groups.length - 1].push(results[i].bool)
        }
    }
    const match = groups.some(group => group.every(Boolean))

    return match ? action : defaultAction
}


/**
 * Calcule le prix réduit d'un produit, source unique de vérité pour TOUS les usages.
 *
 * Retourne TOUJOURS les prix en CENTIMES (entiers) pour éviter les erreurs flottantes.
 * Le caller convertit en unités quand il a besoin d'afficher (`/100`).
 *
 * @param {object} product - prix principal, prix par devise et réduction en % ou fixe
 * @param {object} options - { withPaiement, devise, baseDevise }
 * @returns {{
 *   reduced: boolean,                   // true si la réduction est active maintenant
 *   percent: number | null,             // % si mode pourcentage
 *   amount: number | null,              // montant effectivement déduit si mode fixe
 *   discountCents: number,              // réduction appliquée en centimes
 *   priceCents: number,                 // prix initial en centimes (toujours défini)
 *   reducedPriceCents: number | null,   // prix réduit en centimes (null si pas réduit)
 *   effectivePriceCents: number,        // prix final à utiliser (réduit si reduced, sinon initial)
 *   expiresAt: Date | null              // date d'expiration de la réduction
 * }}
 */
export function computeReducedPrice(product, options = {}) {
    const withPaiement = options.withPaiement !== false
    const devise = options.devise?.toLowerCase()
    const baseDevise = options.baseDevise?.toLowerCase()
    const currencyValue = (field) => {
        if (devise && devise !== baseDevise) {
            const override = product?.[devise]?.[field]
            if (override !== undefined && override !== null && override !== "") return override
        }
        return product?.[field]
    }
    const priceCents = Math.max(0, Math.round(Number(currencyValue("montant") || 0) * 100))
    const fixed = product?.reducedType === "FIXED"
    const amount = fixed ? currencyValue("reducedAmount") : product?.reducedPercent
    const active = withPaiement && priceCents > 0 && product?.activeReducedPrice === true
        && typeof amount === "number" && Number.isFinite(amount) && amount > 0
        && product?.reducedExpireAt
        && Date.now() < new Date(product.reducedExpireAt).getTime()
    const discountCents = active
        ? Math.min(priceCents, fixed ? Math.round(amount * 100) : Math.round(priceCents * amount / 100))
        : 0
    const reduced = discountCents > 0

    return {
        reduced,
        percent: reduced && !fixed ? amount : null,
        amount: reduced && fixed ? discountCents / 100 : null,
        priceCents,
        discountCents,
        reducedPriceCents: reduced ? priceCents - discountCents : null,
        effectivePriceCents: priceCents - discountCents,
        expiresAt: reduced ? new Date(product.reducedExpireAt) : null,
    }
}

export function calculPrice(formValues, formData, devise) {
    const customQuote = quoteCustomPrice({form:formData, values:formValues, devise, baseDevise:formData?.devise || devise, productPrice:computeReducedPrice})
    if (customQuote) return customQuote.total ?? 0
    let total = 0
    const products = formData.products || []

    if (formData.withPaiement) {
        for (const p of products) {
            const { effectivePriceCents } = computeReducedPrice(p, { withPaiement: true, devise, baseDevise: formData?.devise })
            const quantity = formValues[p.id + "_quantity"] || 0
            total += effectivePriceCents * quantity
        }
    }


    //FREE AMOUNT
    for (var k of Object.keys(formValues)) {
        if (k.startsWith("[FREE_AMOUNT]_")) {
            var value = formValues[k]
            if (value > 0) {
                total += Math.round(value * 100)
            }
        }
    }

    return total
}


//EXTRA

/*
    @deprecated
*/
export function applyEditorStyle() {
    return false

    try {
        const drawer = document.getElementsByClassName("css-12i7wg6-MuiPaper-root-MuiDrawer-paper")[0]

        new MutationObserver(function (mutations) {
            for (var item of document.getElementsByClassName("react-page-toolbar-draggable")) {
                const title = item.getElementsByClassName("MuiListItemText-primary")[0]
                const description = item.getElementsByClassName("MuiListItemText-secondary")[0]
                var icon = item.getElementsByClassName("MuiAvatar-root")[0]

                //console.log(title,description,icon)
                if (description.textContent === "Element de formulaire utilisable.") {
                    icon.style.backgroundColor = "#F39C12"
                }

                if (title.textContent === "Block Identitaire") {
                    icon.style.backgroundColor = "#138D75"
                }

                if (title.textContent === "Étapes") {
                    icon.style.backgroundColor = "#BA4A00"
                }

                if (title.textContent === "Étape") {
                    icon.style.backgroundColor = "#BA4A00"
                }

                if (title.textContent === "Condition") {
                    icon.style.backgroundColor = "#2980B9"
                }
            }
        }).observe(drawer, {attributes: true, childList: true, subtree: true, characterData: true})

    } catch (e) {
        console.error(e)
    }
}
